import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ClinicDialog } from '../components/dialogs/ClinicDialog';
import ConfirmDeleteDialog from '../components/dialogs/ConfirmDeleteDialog';
import { CLINIC_DIALOG_SX } from '../constants/popupStyles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { RowContextMenu, ContextMenuOption } from '../components/ui/RowContextMenu';
import { useAuth } from '../context/AuthContext';
import {
  userPrivilegeService,
  UserPrivilege,
  UserPrivilegeCreate,
  UserPrivilegeUpdate
} from '../services/userPrivilegeService';
import ManageColumnsModal from '../components/modals/ManageColumnsModal';
import SaveViewModal from '../components/modals/SaveViewModal';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import MobileCardGrid from '../components/table/MobileCardGrid';
import TablePageLayout from '../components/table/TablePageLayout';
import useTablePage from '../components/table/hooks/useTablePage';
import { privilegeColumns } from '../config/privilegeColumns';
import { formatDate } from '../utils/dateUtils';
import { useCrudHandlers } from '../hooks/useCrudHandlers';

const BoolChip: React.FC<{ value: boolean }> = ({ value }) => (
  <Chip
    label={value ? 'Yes' : 'No'}
    color={value ? 'success' : 'default'}
    size="small"
  />
);

const UserPrivilegesPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Entity-specific state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingPrivilege, setEditingPrivilege] = useState<UserPrivilege | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<UserPrivilegeCreate & UserPrivilegeUpdate>();

  const getSortValue = useCallback((privilege: UserPrivilege, key: string): any => {
    switch (key) {
      case 'name': return privilege.name.toLowerCase();
      case 'manageUsers': return privilege.manage_users ? 1 : 0;
      case 'allPatients': return privilege.all_patients ? 1 : 0;
      case 'questionnaires': return privilege.questionnaires ? 1 : 0;
      case 'emailSmsTemplates': return privilege.email_and_sms_templates ? 1 : 0;
      case 'config': return privilege.config ? 1 : 0;
      case 'configSync': return privilege.config_sync ? 1 : 0;
      case 'fileTypes': return privilege.file_types ? 1 : 0;
      case 'taskTypes': return privilege.task_types ? 1 : 0;
      case 'wellness': return privilege.wellness ? 1 : 0;
      case 'promptDevelopment': return privilege.prompt_development ? 1 : 0;
      case 'docAssistant': return privilege.doc_assistant ? 1 : 0;
      case 'clinicLogin': return privilege.clinic_login ? 1 : 0;
      case 'hmo': return privilege.hmo ? 1 : 0;
      case 'referral': return privilege.referral ? 1 : 0;
      case 'leads': return privilege.leads ? 1 : 0;
      case 'view_video_rooms': return privilege.view_video_rooms ? 1 : 0;
      case 'createdAt': return privilege.created_at;
      default: return '';
    }
  }, []);

  const getSearchText = useCallback((privilege: UserPrivilege): string[] => {
    return [privilege.name];
  }, []);

  const table = useTablePage<UserPrivilege>({
    entityType: 'privileges',
    columns: privilegeColumns,
    tenantId: currentUser?.tenant_id,
    getSortValue,
    getSearchText,
  });

  const crud = useCrudHandlers<UserPrivilege>({
    table,
    createFn: (data) => userPrivilegeService.createUserPrivilege(data),
    updateFn: (id, data) => userPrivilegeService.updateUserPrivilege(id as number, data),
    deleteFn: (id) => userPrivilegeService.deleteUserPrivilege(id as number),
    entityName: 'Privilege',
    onSuccess: () => { setAddDialogOpen(false); setEditingPrivilege(null); reset(); },
    onDeleteSuccess: () => { setDeleteDialogOpen(false); },
  });

  // Load data
  useEffect(() => {
    if (currentUser?.user_privilege?.manage_users) {
      table.loadData(() => userPrivilegeService.getUserPrivileges());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Handlers
  const handleEdit = () => {
    if (table.selectedItem) {
      setEditingPrivilege(table.selectedItem);
      reset({
        name: table.selectedItem.name,
        manage_users: table.selectedItem.manage_users,
        all_patients: table.selectedItem.all_patients,
        questionnaires: table.selectedItem.questionnaires,
        email_and_sms_templates: table.selectedItem.email_and_sms_templates,
        config: table.selectedItem.config,
        config_sync: table.selectedItem.config_sync,
        file_types: table.selectedItem.file_types,
        task_types: table.selectedItem.task_types,
        wellness: table.selectedItem.wellness,
        prompt_development: table.selectedItem.prompt_development,
        doc_assistant: table.selectedItem.doc_assistant,
        clinic_login: table.selectedItem.clinic_login,
        hmo: table.selectedItem.hmo,
        referral: table.selectedItem.referral,
        leads: table.selectedItem.leads,
        view_video_rooms: table.selectedItem.view_video_rooms,
      });
      setAddDialogOpen(true);
    }
    table.handleMenuClose();
  };

  const handleDelete = () => {
    if (table.selectedItem) {
      setDeleteDialogOpen(true);
    }
    table.handleMenuClose();
  };

  const handleAddPrivilege = () => {
    setEditingPrivilege(null);
    reset({
      name: '',
      manage_users: false,
      all_patients: false,
      questionnaires: false,
      email_and_sms_templates: false,
      config: false,
      config_sync: false,
      file_types: false,
      task_types: false,
      wellness: false,
      prompt_development: false,
      doc_assistant: false,
      clinic_login: true,
      hmo: false,
      referral: false,
      leads: false,
      view_video_rooms: false,
    });
    setAddDialogOpen(true);
  };

  const handleNameClick = (privilege: UserPrivilege) => {
    table.handleMenuClose();
    setEditingPrivilege(privilege);
    reset({
      name: privilege.name,
      manage_users: privilege.manage_users,
      all_patients: privilege.all_patients,
      questionnaires: privilege.questionnaires,
      email_and_sms_templates: privilege.email_and_sms_templates,
      config: privilege.config,
      config_sync: privilege.config_sync,
      file_types: privilege.file_types,
      task_types: privilege.task_types,
      wellness: privilege.wellness,
      prompt_development: privilege.prompt_development,
      doc_assistant: privilege.doc_assistant,
      clinic_login: privilege.clinic_login,
      hmo: privilege.hmo,
      referral: privilege.referral,
      leads: privilege.leads,
      view_video_rooms: privilege.view_video_rooms,
    });
    setAddDialogOpen(true);
  };

  const onSubmit = (data: UserPrivilegeCreate & UserPrivilegeUpdate) => {
    crud.handleCreateOrUpdate(data, editingPrivilege);
  };

  const handleConfirmDelete = () => {
    crud.handleDelete(table.selectedItem);
  };

  const getContextMenuOptions = (): ContextMenuOption[] => {
    if (!table.selectedItem) return [];
    return [
      {
        label: 'Edit',
        icon: <EditIcon sx={{ mr: 2 }} />,
        onClick: handleEdit,
      },
      {
        label: 'Delete',
        icon: <DeleteIcon sx={{ mr: 2 }} />,
        onClick: handleDelete,
        color: 'error',
      },
    ];
  };

  // Column value renderer
  const getColumnValue = (privilege: UserPrivilege, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'name':
        return (
          <Typography
            variant="body2"
            onClick={(e) => {
              e.stopPropagation();
              handleNameClick(privilege);
            }}
            sx={{
              cursor: 'pointer',
              textDecoration: 'underline',
              color: 'primary.main',
              '&:hover': { color: 'primary.dark' },
            }}
          >
            {privilege.name}
          </Typography>
        );
      case 'manageUsers': return <BoolChip value={privilege.manage_users} />;
      case 'allPatients': return <BoolChip value={privilege.all_patients} />;
      case 'questionnaires': return <BoolChip value={privilege.questionnaires} />;
      case 'emailSmsTemplates': return <BoolChip value={privilege.email_and_sms_templates} />;
      case 'config': return <BoolChip value={privilege.config} />;
      case 'configSync': return <BoolChip value={privilege.config_sync} />;
      case 'fileTypes': return <BoolChip value={privilege.file_types} />;
      case 'taskTypes': return <BoolChip value={privilege.task_types} />;
      case 'wellness': return <BoolChip value={privilege.wellness} />;
      case 'promptDevelopment': return <BoolChip value={privilege.prompt_development} />;
      case 'docAssistant': return <BoolChip value={privilege.doc_assistant} />;
      case 'clinicLogin': return <BoolChip value={privilege.clinic_login} />;
      case 'hmo': return <BoolChip value={privilege.hmo} />;
      case 'referral': return <BoolChip value={privilege.referral} />;
      case 'leads': return <BoolChip value={privilege.leads} />;
      case 'view_video_rooms': return <BoolChip value={privilege.view_video_rooms} />;
      case 'createdAt': return formatDate(privilege.created_at);
      default: return 'N/A';
    }
  };

  // Mobile card renderer
  const renderMobileCard = (privilege: UserPrivilege) => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} sx={{ pr: 5 }}>
        <Box display="flex" alignItems="center" flex={1}>
          <SecurityIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.5rem' }} />
          <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
            {privilege.name}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ pl: 4, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {privilege.manage_users && <Chip label="Manage Users" size="small" color="success" />}
        {privilege.all_patients && <Chip label="All Patients" size="small" color="success" />}
        {privilege.questionnaires && <Chip label="Questionnaires" size="small" color="success" />}
        {privilege.email_and_sms_templates && <Chip label="Email & SMS" size="small" color="success" />}
        {privilege.config && <Chip label="Config" size="small" color="success" />}
        {privilege.config_sync && <Chip label="Config Sync" size="small" color="success" />}
        {privilege.file_types && <Chip label="File Types" size="small" color="success" />}
        {privilege.task_types && <Chip label="Task Types" size="small" color="success" />}
        {privilege.wellness && <Chip label="Wellness" size="small" color="success" />}
        {privilege.prompt_development && <Chip label="Prompt Dev" size="small" color="success" />}
        {privilege.doc_assistant && <Chip label="Doc Assistant" size="small" color="success" />}
        {privilege.clinic_login && <Chip label="Clinic Login" size="small" color="success" />}
        {privilege.hmo && <Chip label="HMO" size="small" color="success" />}
        {privilege.referral && <Chip label="Referral" size="small" color="success" />}
        {privilege.leads && <Chip label="Leads" size="small" color="success" />}
        {privilege.view_video_rooms && <Chip label="Video Rooms" size="small" color="success" />}
      </Box>
    </>
  );

  if (!currentUser?.user_privilege?.manage_users) {
    return (
      <Alert severity="error">
        You don't have permission to access this page.
      </Alert>
    );
  }

  return (
    <TablePageLayout loading={table.loading} error={table.error} errorPrefix="Error loading privileges:">
      <DataTableToolbar
        {...table.toolbarProps}
        title="User Privileges"
        searchPlaceholder="Search privileges by name..."
        isAdmin={!!currentUser?.user_privilege?.config}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddPrivilege}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Add Privilege
          </Button>
        }
      />

      {isMobile ? (
        <MobileCardGrid
          data={table.sortedData}
          renderCard={renderMobileCard}
          onMenuClick={table.handleRowClick}
          getItemKey={(p) => p.id}
          emptyIcon={<SecurityIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />}
          emptyMessage="No privileges found"
        />
      ) : (
        <DataTableContainer {...table.containerProps}>
          <DataTableBody
            data={table.sortedData}
            {...table.tableBodyProps}
            getColumnValue={getColumnValue}
            onRowClick={table.handleRowClick}
            getRowKey={(p) => p.id}
          />
        </DataTableContainer>
      )}

      <RowContextMenu {...table.menuProps} options={getContextMenuOptions()} />

      {/* Add/Edit Privilege Dialog */}
      <ClinicDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        title={editingPrivilege ? 'Edit Privilege' : 'Add New Privilege'}
        onSubmit={handleSubmit(onSubmit)}
        actions={
          <>
            <Button type="submit" variant="contained" sx={CLINIC_DIALOG_SX.primaryButton}>
              {editingPrivilege ? 'Update' : 'Create'}
            </Button>
            <Button onClick={() => setAddDialogOpen(false)} sx={CLINIC_DIALOG_SX.cancelButton}>
              Cancel
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={CLINIC_DIALOG_SX.fieldRow}>
            <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
              Name
            </Typography>
            <TextField
              {...register('name', { required: 'Privilege name is required' })}
              fullWidth
              size="small"
              error={!!errors.name}
              helperText={errors.name?.message as string}
              InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
            />
          </Box>

          <Box mt={1}>
            <Typography variant="body2" sx={{ ...CLINIC_DIALOG_SX.fieldLabel, mb: 1 }}>
              Permissions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <FormControlLabel
                control={<Checkbox {...register('manage_users')} defaultChecked={editingPrivilege?.manage_users || false} />}
                label="Manage Users"
              />
              <FormControlLabel
                control={<Checkbox {...register('all_patients')} defaultChecked={editingPrivilege?.all_patients || false} />}
                label="All Patients"
              />
              <FormControlLabel
                control={<Checkbox {...register('questionnaires')} defaultChecked={editingPrivilege?.questionnaires || false} />}
                label="Questionnaires"
              />
              <FormControlLabel
                control={<Checkbox {...register('email_and_sms_templates')} defaultChecked={editingPrivilege?.email_and_sms_templates || false} />}
                label="Email & SMS Templates"
              />
              <FormControlLabel
                control={<Checkbox {...register('config')} defaultChecked={editingPrivilege?.config || false} />}
                label="Config"
              />
              <FormControlLabel
                control={<Checkbox {...register('config_sync')} defaultChecked={editingPrivilege?.config_sync || false} />}
                label="Config Sync"
              />
              <FormControlLabel
                control={<Checkbox {...register('file_types')} defaultChecked={editingPrivilege?.file_types || false} />}
                label="File Types"
              />
              <FormControlLabel
                control={<Checkbox {...register('task_types')} defaultChecked={editingPrivilege?.task_types || false} />}
                label="Task Types"
              />
              <FormControlLabel
                control={<Checkbox {...register('wellness')} defaultChecked={editingPrivilege?.wellness || false} />}
                label="Wellness"
              />
              <FormControlLabel
                control={<Checkbox {...register('prompt_development')} defaultChecked={editingPrivilege?.prompt_development || false} />}
                label="Prompt Development"
              />
              <FormControlLabel
                control={<Checkbox {...register('doc_assistant')} defaultChecked={editingPrivilege?.doc_assistant || false} />}
                label="Doc Assistant"
              />
              <FormControlLabel
                control={<Checkbox {...register('clinic_login')} defaultChecked={editingPrivilege ? editingPrivilege.clinic_login : true} />}
                label="Clinic Login"
              />
              <FormControlLabel
                control={<Checkbox {...register('hmo')} defaultChecked={editingPrivilege?.hmo || false} />}
                label="HMO"
              />
              <FormControlLabel
                control={<Checkbox {...register('referral')} defaultChecked={editingPrivilege?.referral || false} />}
                label="Referral"
              />
              <FormControlLabel
                control={<Checkbox {...register('leads')} defaultChecked={editingPrivilege?.leads || false} />}
                label="Leads"
              />
              <FormControlLabel
                control={<Checkbox {...register('view_video_rooms')} defaultChecked={editingPrivilege?.view_video_rooms || false} />}
                label="Video Rooms"
              />
            </Box>
          </Box>
        </Box>
      </ClinicDialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); table.handleMenuClose(); }}
        onConfirm={handleConfirmDelete}
        entityType="Privilege"
        entityName={table.selectedItem?.name}
      />

      <ManageColumnsModal {...table.manageColumnsProps} />
      <SaveViewModal {...table.saveViewModalProps} isAdmin={!!currentUser?.user_privilege?.config} />
    </TablePageLayout>
  );
};

export default UserPrivilegesPage;
